import { LayoutSidebar } from '@/components/layouts/LayoutSidebar';
import { Button } from '@/components/ui/button';
import EventComposer from '@/features/calendar/EventComposer';
import MessageComposer from '@/features/messaging/MessageComposer';
import { ChatUnreadBadge } from '@/features/speak2me/components/ChatUnreadBadge';
import { useUser } from '@/hooks';
import { useSearchStore } from '@/store/searchStore';
import { useSpeak2MeStore } from '@/store/speak2meStore';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

function LocationListener() {
  const location = useLocation();
  const isContentDetailRoute = location.pathname.startsWith('/content/');
  const setLayoutMode = useSearchStore((state) => state.setLayoutMode);

  useEffect(() => {
    setLayoutMode(isContentDetailRoute);
  }, [isContentDetailRoute, location.pathname, setLayoutMode]);

  return null;
}

function CreateSpeak2MeButton({ isOpen }: { isOpen: boolean }) {
  const setIsOpen = useSpeak2MeStore((state) => state.setIsOpen);
  const setView = useSpeak2MeStore((state) => state.setView);
  const setOpenAsFreshChat = useSpeak2MeStore((state) => state.setOpenAsFreshChat);
  const setOpenInDefaultInputMode = useSpeak2MeStore((state) => state.setOpenInDefaultInputMode);
  if (isOpen) {
    return null;
  }

  const onOpenChat = () => {
    setIsOpen(true);
    setView('chat');
    setOpenAsFreshChat(true);
    setOpenInDefaultInputMode(true);
  };
  const onOpenUnreadList = () => {
    setIsOpen(true);
    setView('unread-list');
  };
  return (
    <div data-testid="layout-create-obs-btn" className="pointer-events-auto">
      <div className="relative">
        <Button
          data-testid="speak2me-open-button"
          size="lg"
          onClick={onOpenChat}
          className="w-48 rounded-full shadow-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <span className="px-3 retro-2000s-font text-lg">Speak2Me</span>
        </Button>
        <ChatUnreadBadge className="absolute -top-1 -right-1 z-10" onClick={onOpenUnreadList} />
      </div>
    </div>
  );
}

interface Screen {
  screen: 'ANCHOR' | 'DOCUMENT' | 'EVENT' | 'photo' | 'recording' | 'video';
  id: number;
}

export interface ExploreOutletContext {
  setScreen: (screen: Screen | null) => void;
  screen: Screen | null;
}

export default function Layout() {
  const [screen, setScreen] = useState<Screen | null>(null);
  const isOpen = useSpeak2MeStore((state) => state.isOpen);
  const location = useLocation();
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const { data: memberContext, isLoading } = useUser();

  const setScreenSafe = useCallback(
    (next: Screen | null) => {
      setScreen((prev) => {
        const isSame =
          prev?.screen === next?.screen &&
          prev?.id === next?.id;

        if (isSame) {
          return prev;
        }

        return next;
      });
    },
    []
  );

  useEffect(() => {
    if (!viewportRef.current) {
      return;
    }
    viewportRef.current.scrollTop = 0;
  }, [location.pathname]);

  // Only block layout if we're still loading auth data or if there's no authenticated user at all
  if (isLoading || !memberContext) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 overflow-hidden">
      <main className="flex h-full min-h-0 min-w-0 flex-1 overflow-hidden">
        <div className="hidden h-full w-[290px] flex-shrink-0 flex-col overflow-y-auto overflow-x-hidden border-r p-4 pb-24 lg:flex">
          <LayoutSidebar />
        </div>
        <div
          ref={viewportRef}
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden"
        >
          <Outlet key={location.pathname} context={{ setScreen: setScreenSafe, screen }} />
        </div>
      </main>

      <div className="pointer-events-none fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        <CreateSpeak2MeButton isOpen={isOpen} />
        <MessageComposer />
        <EventComposer />
      </div>
      <LocationListener />
    </div>
  );
}

